import { useCallback, useRef, useState } from "react";
import usePfqAxios from "./usePfqAxios";
import { classifyPreviewStreamResult } from "../utils/previewStreamOutcome";
import { toaster } from "../utils/toast";

const REGENERATION_FAILED_MESSAGE = "Regeneration failed.";
const REGENERATION_FINISHED_MESSAGE = "Regeneration finished.";
const NO_CASE_SKIPPED_MESSAGE = "No case to regenerate.";

/**
 * Toasts the outcome of a resolved `regenerate()` result. Shared by callers (e.g. a studio's
 * guarded close) that want immediate success/failure feedback rather than a bespoke outcome
 * classification like preview's. A stream that closes without a terminal frame resolves falsy,
 * which reads as a failure rather than throwing past the caller.
 * @param {{ success: boolean, message?: string }} [result]
 */
export const toastRegenerationResult = (result) => {
  const isSuccess = Boolean(result?.success);
  const fallbackMessage = isSuccess ? REGENERATION_FINISHED_MESSAGE : REGENERATION_FAILED_MESSAGE;
  toaster({ message: result?.message || fallbackMessage, success: isSuccess });
};

/**
 * Coordinates toast ownership for a shared `regenerate()` request so exactly one caller
 * announces the outcome, even when several callers end up awaiting the same in-flight
 * promise. A caller with its own outcome handling (Preview, guarded close) calls
 * `claimOutcomeHandling(promise)` right after `regenerate()` instead of toasting via this
 * hook. Every other caller (the after-edit trigger, the hard-navigation unmount fallback)
 * calls `regenerateSilently()` instead of `regenerate()` directly, and gets a default toast
 * unless a dedicated caller claims that same request, another silent caller already claimed
 * it first, or the regeneration found nothing to do.
 * @param {Function} regenerate
 */
export const useSharedRegenerationOutcome = (regenerate) => {
  const dedicatedOutcomePromiseRef = useRef(null);
  const firstSilentClaimPromiseRef = useRef(null);

  // a regeneration that found nothing dirty is not an outcome worth interrupting anyone for,
  // and a silent trigger firing right after a guarded close reliably lands here
  const hasNothingToReport = (result) =>
    Boolean(result?.skipped) || classifyPreviewStreamResult(result) === "upToDate";

  const claimOutcomeHandling = useCallback((promise) => {
    dedicatedOutcomePromiseRef.current = promise;
  }, []);

  const regenerateSilently = useCallback(async () => {
    const regenerationPromise = regenerate();
    const isFirstSilentCaller = firstSilentClaimPromiseRef.current !== regenerationPromise;
    if (isFirstSilentCaller) {
      firstSilentClaimPromiseRef.current = regenerationPromise;
    }
    const result = await regenerationPromise;
    const isOutcomeUnclaimed =
      isFirstSilentCaller && dedicatedOutcomePromiseRef.current !== regenerationPromise;
    if (isOutcomeUnclaimed && !hasNothingToReport(result)) {
      toastRegenerationResult(result);
    }
    return result;
  }, [regenerate]);

  return { regenerateSilently, claimOutcomeHandling };
};

/**
 * Fires a case regeneration at most once per studio session, streaming SSE events until
 * finished. `regenerate()` never rejects -- it resolves `{ success, message }` so every
 * caller (preview, auto-regenerate-on-edit, guarded close) gets an outcome to act on instead
 * of needing its own `.catch()`.
 * @param {{ caseId: string, endpoint: string }} params
 */
const useStudioRegeneration = ({ caseId, endpoint }) => {
  const { streamPost } = usePfqAxios();
  const [isRegenerating, setIsRegenerating] = useState(false);
  const requestPromiseRef = useRef(null);

  const regenerate = useCallback(({ onStarted } = {}) => {
    if (!caseId) {
      return Promise.resolve({ success: true, skipped: true, message: NO_CASE_SKIPPED_MESSAGE });
    }
    if (requestPromiseRef.current) {
      return requestPromiseRef.current;
    }
    setIsRegenerating(true);
    requestPromiseRef.current = streamPost(endpoint, { caseId }, {
      onEvent: (payload) => {
        if (payload?.message?.includes("started")) {
          onStarted?.(payload);
        }
      },
    })
      .catch((err) => ({ success: false, message: err?.message || REGENERATION_FAILED_MESSAGE }))
      .finally(() => {
        setIsRegenerating(false);
        requestPromiseRef.current = null;
      });

    return requestPromiseRef.current;
  }, [caseId, endpoint, streamPost]);

  return { regenerate, isRegenerating };
};

export default useStudioRegeneration;
